import { useLayoutEffect, useState } from 'react';

import { Shadows } from '@mui/material';
import { FieldValues, useFormContext } from 'react-hook-form';
import { isDeepEqual } from 'remeda';
import {
  DecodedValueMap,
  QueryParamConfigMap,
  UrlUpdateType
} from 'use-query-params';

import { useTabsInit } from './useTabsInit';

interface IUseFiltersSyncProps<
  T extends string,
  P extends QueryParamConfigMap,
  F extends FieldValues
> {
  queryParams: DecodedValueMap<P>;
  safeActiveTab: T;
  currentActiveTab: string;
  setCurrentActiveTab: (tab: string) => void;
  defaultFilterValues: F;
  changePerPage: (perPage: number, urlUpdateType?: UrlUpdateType) => void;
  changePage: (page: number, urlUpdateType?: UrlUpdateType) => void;
  onResetChange?: () => void;
}

interface IUseTabsAndFiltersSyncProps<
  T extends string,
  P extends QueryParamConfigMap,
  F extends FieldValues
> {
  tabsKeys: readonly T[];
  queryParams: DecodedValueMap<P>;
  defaultFilterValues: F;
  filtersQueryParamsConfig: P;
  paginationInfo: {
    changePerPage: (perPage: number) => void;
    changePage: (page: number) => void;
  };
  onResetChange?: () => void;
  mainActiveTab: string;
}

/**
 * Хук для синхронизации табов и фильтров
 * @param root0 - пропсы
 * @param root0.tabsKeys - ключи табов
 * @param root0.queryParams - query-параметры
 * @param root0.defaultFilterValues - значения по умолчанию для фильтров
 * @param root0.paginationInfo - информация о пагинации
 * @param root0.onResetChange - функция для выполнения при сбросе фильтров
 * @param root0.mainActiveTab - основной таб
 */
export const useTabsAndFiltersSync = <
  T extends string,
  P extends QueryParamConfigMap,
  F extends FieldValues
>({
  tabsKeys,
  queryParams,
  defaultFilterValues,
  paginationInfo,
  mainActiveTab,
  onResetChange
}: IUseTabsAndFiltersSyncProps<T, P, F>) => {
  const {
    safeActiveTab: safeSecondActiveTab,
    setActiveTab: setSecondActiveFilterTab
  } = useTabsInit<T>(tabsKeys, {
    paramName: 'second_tab',
    urlSearchParams: {
      tab: mainActiveTab
    }
  });

  const [currentSecondActiveTab, setCurrentSecondActiveTab] =
    useState<string>(safeSecondActiveTab);

  const { isFilterOptionsReady } = useFiltersSync({
    queryParams,
    safeActiveTab: safeSecondActiveTab,
    currentActiveTab: currentSecondActiveTab,
    setCurrentActiveTab: setCurrentSecondActiveTab,
    defaultFilterValues,
    changePerPage: paginationInfo.changePerPage,
    changePage: paginationInfo.changePage,
    onResetChange
  });

  return {
    safeSecondActiveTab,
    setSecondActiveFilterTab,
    isFilterOptionsReady
  };
};

/**
 * Перезаполняет форму из query-параметров при каждой их смене (`reset` в
 * `useLayoutEffect` — до отрисовки). `isFilterOptionsReady` — true после ПЕРВОЙ
 * синхронизации и дальше не сбрасывается: панель фильтров не должна
 * размонтироваться при пересинхронизации с адресом, иначе теряются раскрытые
 * секции и прокрутка.
 * @param root0 - пропсы
 * @param root0.queryParams - query-параметры
 * @param root0.defaultFilterValues - значения по умолчанию для фильтров
 * @param root0.extraDeps - доп. зависимости, меняющие которые тоже запускают синхронизацию
 * @param root0.onBeforeSync - вызывается перед каждой пересинхронизацией
 */
export const useQueryFormSync = <
  P extends QueryParamConfigMap,
  F extends FieldValues
>({
  queryParams,
  defaultFilterValues,
  extraDeps = [],
  onBeforeSync
}: {
  queryParams: DecodedValueMap<P>;
  defaultFilterValues: F;
  extraDeps?: unknown[];
  onBeforeSync?: () => void;
}) => {
  const { reset } = useFormContext();
  // Нужна ли (пере)синхронизация формы с адресом
  const [needsSync, setNeedsSync] = useState(true);
  // Была ли хоть одна синхронизация (панель можно рендерить)
  const [isFilterOptionsReady, setIsFilterOptionsReady] = useState(false);

  useLayoutEffect(() => {
    onBeforeSync?.();
    setNeedsSync(true);
  }, [queryParams, reset, ...extraDeps]);

  useLayoutEffect(() => {
    if (needsSync) {
      const queryValues = parseQueryParamsToFormValues(queryParams);

      reset({ ...defaultFilterValues, ...queryValues });

      setNeedsSync(false);
      setIsFilterOptionsReady(true);
    }
  }, [needsSync]);

  return { isFilterOptionsReady };
};

/**
 * Хук для синхронизации фильтров с query-параметрами.
 * `isFilterOptionsReady` — true после ПЕРВОЙ синхронизации и дальше не сбрасывается
 * @param root0 - пропсы
 * @param root0.queryParams - query-параметры
 * @param root0.safeActiveTab - безопасный активный таб
 * @param root0.currentActiveTab - текущий активный таб
 * @param root0.defaultFilterValues - значения по умолчанию для фильтров
 * @param root0.onResetChange - функция для выполнения при сбросе фильтров
 */
export const useFiltersSync = <
  T extends string,
  P extends QueryParamConfigMap,
  F extends FieldValues
>({
  queryParams,
  safeActiveTab,
  currentActiveTab,
  defaultFilterValues,
  onResetChange
}: IUseFiltersSyncProps<T, P, F>) => {
  const { isFilterOptionsReady } = useQueryFormSync({
    queryParams,
    defaultFilterValues,
    extraDeps: [safeActiveTab],
    /** Смена вкладки перед пересинхронизацией — сброс фильтров вкладки, как раньше */
    onBeforeSync: () => {
      if (safeActiveTab !== currentActiveTab) {
        onResetChange?.();
      }
    }
  });

  return { isFilterOptionsReady };
};

/**
 * Возвращает стили для карточки фильтров.
 * `overflow: visible` обязателен: у MUI Card дефолтный `overflow: hidden`, из-за
 * которого sticky-футер с кнопкой применения (`StickyFiltersFooter`) не прилипает.
 * `pb: 0` — нижний отступ даёт сам футер; со своим падингом карточки под кнопкой
 * копилось вдвое больше пустого места. Панели без футера (мобильная шторка) держат
 * отступ внутренним `Stack`
 *
 * Ширина рельсы включается с **md (900)** — на том же пороге, где панель выходит
 * из шторки и встаёт строкой рядом со списком. Раньше фикс был на `lg` (1200), и
 * в зазоре 900–1199 карточка оставалась `width: 100%`, то есть во flex-строке
 * забирала до 55% ряда, а ячейка списка падала с 416 до 175px.
 * @param shadows - тени
 * @param root0 - настройки поверхности
 * @param root0.flat - панель внутри шторки: без радиуса и тени
 */
export const getFiltersCardStyles = (
  shadows: Shadows,
  { flat = false }: { flat?: boolean } = {}
) => ({
  borderWidth: 0,
  p: 0,
  pb: 0,
  width: { xs: '100%', md: 390 },
  // Рельса не тянется и не сжимается: во flex-строке её размер целиком задаёт
  // `width`. Записано тройкой flexGrow/flexShrink/flexBasis, а не `flex: '0 0
  // 390px'`, потому что часть консьюмеров держит эту карточку внутри
  // КОЛОНОЧНОГО Stack — там flex-basis ушёл бы в высоту и запер её на 390px.
  flexGrow: 0,
  flexShrink: 0,
  flexBasis: 'auto',
  height: 'auto',
  overflow: 'visible',
  boxShadow: shadows[2],
  // Внутри шторки панель лежит на поверхности sheet'а: её собственный радиус и
  // тень читались вторым слоем поверх чужого скругления. Одна поверхность —
  // один радиус и одна тень
  ...(flat && { borderRadius: 0, boxShadow: 'none', width: '100%' })
});

/**
 * Проверяет, нужно ли сбрасывать значение параметра
 * @param value - значение параметра
 * @param defaultValue - значение по умолчанию
 * @returns true если значение нужно сбросить
 */
export const shouldResetValue = <T>(
  value: T,
  defaultValue: T | undefined
): boolean => {
  return defaultValue !== undefined && isDeepEqual(value, defaultValue);
};

/**
 * Считает поля формы фильтров, отличающиеся от значений по умолчанию, —
 * для бейджа активных фильтров в свёрнутой части панели кастингов.
 * @param values - текущие значения формы
 * @param defaults - значения по умолчанию
 * @param excludeKeys - ключи, не участвующие в подсчёте (видимые группы)
 */
export const countChangedFilters = (
  values: Record<string, unknown>,
  defaults: Record<string, unknown>,
  excludeKeys: readonly string[] = []
): number =>
  Object.keys(defaults).filter(
    key =>
      !excludeKeys.includes(key) && !isDeepEqual(values[key], defaults[key])
  ).length;

/**
 * Проверяет, является ли значение фильтра пустым (не несущим смысла)
 * Булево `false` НЕ считается пустым — это осознанное значение
 * (например, `job_is_short_film: false`). Число `0` считается пустым,
 * т.к. используется как sentinel-значение по умолчанию (например,
 * `defaultSalaryFilter`) и не должно самостоятельно попадать в URL.
 * @param value - значение параметра
 * @returns true если значение пустое и должно быть удалено из query-параметров
 */
const isEmptyFilterValue = (value: unknown): boolean =>
  value === null ||
  value === undefined ||
  value === '' ||
  value === 0 ||
  (Array.isArray(value) && value.length === 0);

/**
 * Прижимает диапазон `[от, до]` к границам шкалы `[min, max]`: значение вне шкалы
 * (набранное руками, из ручного URL или пресета) не должно сужать выдачу молча.
 * Не диапазон (не пара чисел) возвращается как есть
 * @param value - значение поля
 * @param bounds - шкала `[min, max]`
 */
export const clampRangeToBounds = <T>(value: T, bounds: unknown): T => {
  if (
    !Array.isArray(value) ||
    value.length !== 2 ||
    !Array.isArray(bounds) ||
    bounds.length !== 2 ||
    ![...value, ...bounds].every(item => typeof item === 'number')
  )
    return value;

  const [min, max] = bounds as number[];
  /**
   *
   * @param item
   */
  const from = Math.min(Math.max(value[0], min), max);
  const to = Math.min(Math.max(value[1], min), max);

  return (from > to ? [to, from] : [from, to]) as T;
};

/**
 * Обработка фильтров (Установка/Сброс квери параметров)
 * @param data - данные формы фильтров
 * @param setQueryParams - функция для установки квери параметров
 * @param defaultValues - объект с значениями по умолчанию для сравнения
 * @param defaultKeys - ключи полей, которые нужно сравнивать с значениями по умолчанию
 */
export const processFilters = <T extends Record<string, unknown>>(
  data: T,
  setQueryParams: (params: Record<string, string | undefined>) => void,
  defaultValues: Partial<T> = {},
  defaultKeys: (keyof T)[] = []
): void => {
  for (const key in data) {
    const keyTyped = key as keyof T;
    const defaultValue = defaultValues[keyTyped];

    // Проверяем, нужно ли сравнивать с значением по умолчанию
    const isShouldCheckDefault = defaultKeys.includes(keyTyped);
    // Диапазонный ключ: значение вне шкалы (дефолт = границы) в адрес не уходит
    const value = isShouldCheckDefault
      ? clampRangeToBounds(data[keyTyped], defaultValue)
      : data[keyTyped];

    if (isShouldCheckDefault && shouldResetValue(value, defaultValue)) {
      setQueryParams({ [keyTyped]: undefined });
      continue;
    }

    if (isEmptyFilterValue(value)) {
      setQueryParams({ [keyTyped]: undefined });
    } else {
      const queryValue = JSON.stringify(value);
      setQueryParams({ [keyTyped]: queryValue });
    }
  }
};

/**
 * Парсит query параметры в типизированные значения формы
 * @param queryParams - объект с query параметрами
 * @returns объект с типизированными значениями формы
 */
export const parseQueryParamsToFormValues = <T extends object>(
  queryParams: Record<string, unknown>
): Partial<T> => {
  const formValues: Partial<T> = {};

  for (const key in queryParams) {
    const value = queryParams[key];

    if (value === undefined) continue;

    // Адрес правят руками и приносят из старых ссылок: `?height=80&height=95`
    // или `?chest_girth=80` — не JSON. Такой параметр пропускаем (поле остаётся
    // по умолчанию), иначе JSON.parse роняет всю страницу
    try {
      formValues[key as keyof T] = JSON.parse(value as string);
    } catch {
      // битое значение — игнорируем
    }
  }

  return formValues;
};
